const DB_NAME = "kbo-cache";
const DB_VERSION = 1;
const STORE_NAME = "cache";

class Cache {
  constructor() {
    this.db = null;
    this.initialized = false;
    this.initPromise = this.init();
    this.memoryCache = new Map();
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error("Error opening IndexedDB");
        reject(request.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        this.initialized = true;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
    });
  }

  async ensureInitialized() {
    if (!this.initialized) {
      await this.initPromise;
    }
  }

  async get(key) {
    if (window.localStorage.NO_NETWORK_CACHE === "true") return null;

    // First check in-memory cache
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry) {
      if (memoryEntry.expiresAt > Date.now()) {
        return memoryEntry.value;
      } else {
        // Remove expired entry from memory cache
        this.memoryCache.delete(key);
        // Remove expired entry from IndexedDB
        this.delete(key);
        return null;
      }
    }

    // If not in memory or expired, try IndexedDB
    await this.ensureInitialized();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        if (result && result.expiresAt > Date.now()) {
          // Store in memory cache for future access
          this.memoryCache.set(key, result);
          resolve(result.value);
        } else {
          // If expired, remove the entry
          this.delete(key);
          resolve(null);
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async set(key, value, ttl = 30 * 60 * 1000) {
    // Default TTL: 30 minutes
    const entry = {
      value,
      expiresAt: Date.now() + ttl,
    };

    // Store in memory cache first
    this.memoryCache.set(key, entry);

    // Then store in IndexedDB
    await this.ensureInitialized();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(entry, key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async delete(key) {
    // Remove from memory cache
    this.memoryCache.delete(key);

    // Remove from IndexedDB
    await this.ensureInitialized();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear() {
    // Clear memory cache
    this.memoryCache.clear();

    // Clear IndexedDB
    await this.ensureInitialized();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

// Export a singleton instance
export const cache = new Cache();
